import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { SingleColumnTemplate } from '../../../../../ui/layouts';
import Button from '../../../../../ui/canon/Button';
import Card from '../../../../../ui/canon/Card';
import Input from '../../../../../ui/canon/Input';
import { Badge, FormInput } from '../shared';

const HiringPostForm = ({ post, isEditing, onSave, onPublish, onDelete }) => {
  const intl = useIntl();
  const [formData, setFormData] = useState({
    title: post?.title || '',
    positionType: post?.positionType || '',
    location: post?.location || '',
    remote: post?.remote || false,
    status: post?.status || 'draft',
    description: post?.description || '',
    requirements: post?.requirements || '',
    qualifications: post?.qualifications || '',
    duration: post?.duration || '',
    startDate: post?.startDate || '',
    compensation: post?.compensation || '',
    applicationInstructions: post?.applicationInstructions || '',
    firmName: post?.firmName || '',
    linkedinUrl: post?.linkedinUrl || '',
  });

  const [errors, setErrors] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) newErrors.title = intl.formatMessage({ id: 'hiring.post_form.error.title_required' });
    if (!formData.positionType) newErrors.positionType = intl.formatMessage({ id: 'hiring.post_form.error.position_type_required' });
    if (!formData.location.trim()) newErrors.location = intl.formatMessage({ id: 'hiring.post_form.error.location_required' });
    if (!formData.description.trim()) newErrors.description = intl.formatMessage({ id: 'hiring.post_form.error.description_required' });
    if (formData.description.length > 2000) newErrors.description = intl.formatMessage({ id: 'hiring.post_form.error.description_too_long' });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveDraft = async () => {
    setIsSaving(true);
    try {
      await onSave({ ...formData, status: 'draft' });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      await onPublish({ ...formData, status: formData.status || 'published' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = () => {
    if (confirm(intl.formatMessage({ id: 'hiring.post_form.confirm_delete' }))) {
      onDelete();
    }
  };

  const characterCount = formData.description.length;
  const characterLimit = 2000;

  return (
    <SingleColumnTemplate
      title={isEditing
        ? intl.formatMessage({ id: 'hiring.post_form.title_edit' })
        : intl.formatMessage({ id: 'hiring.post_form.title_create' })}
      subtitle={isEditing
        ? intl.formatMessage({ id: 'hiring.post_form.subtitle_edit' })
        : intl.formatMessage({ id: 'hiring.post_form.subtitle_create' })}
      breadcrumbs={
        <a
          href="/hiring"
          className="inline-flex items-center gap-2 text-sm text-succession-text-secondary hover:text-succession-text-primary transition-colors"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          {intl.formatMessage({ id: 'hiring.post_form.back_link' })}
        </a>
      }
      actions={
        <Button
          variant="outline"
          size="md"
          onClick={() => setShowPreview(!showPreview)}
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          {showPreview
            ? intl.formatMessage({ id: 'hiring.post_form.action.edit' })
            : intl.formatMessage({ id: 'hiring.post_form.action.preview' })}
        </Button>
      }
      variant="wide"
    >

        {!showPreview ? (
          /* Edit Form */
          <div className="bg-white border border-neutral-200 rounded-lg p-6 space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="font-semibold text-neutral-900 mb-4">{intl.formatMessage({ id: 'hiring.post_form.section.basic_info' })}</h3>
              <div className="space-y-4">
                <FormInput
                  label={intl.formatMessage({ id: 'hiring.post_form.field.position_title' })}
                  placeholder={intl.formatMessage({ id: 'hiring.post_form.placeholder.position_title' })}
                  value={formData.title}
                  onChange={(e) => updateFormData('title', e.target.value)}
                  error={errors.title}
                  required
                  size="lg"
                />

                <div>
                  <label className="block text-sm font-medium text-neutral-900 mb-3">
                    {intl.formatMessage({ id: 'hiring.post_form.field.position_type' })} <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                      formData.positionType === 'internship'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-neutral-200 hover:bg-neutral-50'
                    }`}>
                      <input
                        type="radio"
                        name="positionType"
                        value="internship"
                        checked={formData.positionType === 'internship'}
                        onChange={(e) => updateFormData('positionType', e.target.value)}
                        className="h-4 w-4 text-blue-600"
                      />
                      <div>
                        <div className="font-medium text-neutral-900">{intl.formatMessage({ id: 'hiring.post_form.position_type.internship' })}</div>
                        <div className="text-sm text-neutral-600">
                          {intl.formatMessage({ id: 'hiring.post_form.position_type.internship_description' })}
                        </div>
                      </div>
                    </label>
                    <label className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                      formData.positionType === 'full-time'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-neutral-200 hover:bg-neutral-50'
                    }`}>
                      <input
                        type="radio"
                        name="positionType"
                        value="full-time"
                        checked={formData.positionType === 'full-time'}
                        onChange={(e) => updateFormData('positionType', e.target.value)}
                        className="h-4 w-4 text-blue-600"
                      />
                      <div>
                        <div className="font-medium text-neutral-900">{intl.formatMessage({ id: 'hiring.post_form.position_type.full_time' })}</div>
                        <div className="text-sm text-neutral-600">
                          {intl.formatMessage({ id: 'hiring.post_form.position_type.full_time_description' })}
                        </div>
                      </div>
                    </label>
                  </div>
                  {errors.positionType && (
                    <p className="text-sm text-red-500 mt-2 flex items-center gap-1">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {errors.positionType}
                    </p>
                  )}
                </div>

                <FormInput
                  label={intl.formatMessage({ id: 'hiring.post_form.field.firm_name' })}
                  placeholder={intl.formatMessage({ id: 'hiring.post_form.placeholder.firm_name' })}
                  value={formData.firmName}
                  onChange={(e) => updateFormData('firmName', e.target.value)}
                  error={errors.firmName}
                  helperText={intl.formatMessage({ id: 'hiring.post_form.helper.firm_name' })}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput
                    label={intl.formatMessage({ id: 'hiring.post_form.field.location' })}
                    placeholder={intl.formatMessage({ id: 'hiring.post_form.placeholder.location' })}
                    value={formData.location}
                    onChange={(e) => updateFormData('location', e.target.value)}
                    error={errors.location}
                    required
                  />

                  <div className="flex items-end">
                    <label className="flex items-center gap-2 h-12 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.remote}
                        onChange={(e) => updateFormData('remote', e.target.checked)}
                        className="h-4 w-4 rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-neutral-900">
                        {intl.formatMessage({ id: 'hiring.post_form.field.remote_option' })}
                      </span>
                    </label>
                  </div>
                </div>

                <FormInput
                  label={intl.formatMessage({ id: 'hiring.post_form.field.linkedin_url' })}
                  type="url"
                  placeholder={intl.formatMessage({ id: 'hiring.post_form.placeholder.linkedin_url' })}
                  value={formData.linkedinUrl}
                  onChange={(e) => updateFormData('linkedinUrl', e.target.value)}
                  helperText={intl.formatMessage({ id: 'hiring.post_form.helper.linkedin_url' })}
                />
              </div>
            </div>

            {/* Description */}
            <div className="border-t border-neutral-200 pt-6">
              <label className="block text-sm font-medium text-neutral-900 mb-2">
                {intl.formatMessage({ id: 'hiring.post_form.field.description' })} <span className="text-red-500">*</span>
              </label>
              <textarea
                rows={6}
                maxLength={2000}
                value={formData.description}
                onChange={(e) => updateFormData('description', e.target.value)}
                placeholder={intl.formatMessage({ id: 'hiring.post_form.placeholder.description' })}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.description ? 'border-red-500' : 'border-neutral-300'
                }`}
              />
              <div className="flex justify-between items-center mt-2">
                <p className="text-sm text-neutral-500">{intl.formatMessage({ id: 'hiring.post_form.helper.description' })}</p>
                <p className={`text-sm ${characterCount > characterLimit ? 'text-red-500' : 'text-neutral-500'}`}>
                  {characterCount}/{characterLimit}
                </p>
              </div>
              {errors.description && (
                <p className="text-sm text-red-500 mt-1">{errors.description}</p>
              )}
            </div>

            {/* Requirements */}
            <div className="border-t border-neutral-200 pt-6">
              <label className="block text-sm font-medium text-neutral-900 mb-2">
                {intl.formatMessage({ id: 'hiring.post_form.field.requirements' })}
              </label>
              <textarea
                rows={5}
                maxLength={1000}
                value={formData.requirements}
                onChange={(e) => updateFormData('requirements', e.target.value)}
                placeholder={intl.formatMessage({ id: 'hiring.post_form.placeholder.requirements' })}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex justify-between items-center mt-2">
                <p className="text-sm text-neutral-500">{intl.formatMessage({ id: 'hiring.post_form.helper.requirements' })}</p>
                <p className="text-sm text-neutral-500">{formData.requirements.length}/1000</p>
              </div>
            </div>

            {/* Qualifications */}
            <div className="border-t border-neutral-200 pt-6">
              <label className="block text-sm font-medium text-neutral-900 mb-2">
                {intl.formatMessage({ id: 'hiring.post_form.field.qualifications' })}
              </label>
              <textarea
                rows={4}
                maxLength={500}
                value={formData.qualifications}
                onChange={(e) => updateFormData('qualifications', e.target.value)}
                placeholder={intl.formatMessage({ id: 'hiring.post_form.placeholder.qualifications' })}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex justify-between items-center mt-2">
                <p className="text-sm text-neutral-500">{intl.formatMessage({ id: 'hiring.post_form.helper.qualifications' })}</p>
                <p className="text-sm text-neutral-500">{formData.qualifications.length}/500</p>
              </div>
            </div>

            {/* Position Details */}
            <div className="border-t border-neutral-200 pt-6">
              <h3 className="font-semibold text-neutral-900 mb-4">{intl.formatMessage({ id: 'hiring.post_form.section.position_details' })}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormInput
                  label={intl.formatMessage({ id: 'hiring.post_form.field.duration' })}
                  placeholder={intl.formatMessage({ id: 'hiring.post_form.placeholder.duration' })}
                  value={formData.duration}
                  onChange={(e) => updateFormData('duration', e.target.value)}
                />

                <FormInput
                  label={intl.formatMessage({ id: 'hiring.post_form.field.start_date' })}
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => updateFormData('startDate', e.target.value)}
                />

                <FormInput
                  label={intl.formatMessage({ id: 'hiring.post_form.field.compensation' })}
                  placeholder={intl.formatMessage({ id: 'hiring.post_form.placeholder.compensation' })}
                  value={formData.compensation}
                  onChange={(e) => updateFormData('compensation', e.target.value)}
                  helperText={intl.formatMessage({ id: 'hiring.post_form.helper.compensation' })}
                />

                <FormInput
                  label={intl.formatMessage({ id: 'hiring.post_form.field.linkedin_profile' })}
                  type="url"
                  placeholder={intl.formatMessage({ id: 'hiring.post_form.placeholder.linkedin_url' })}
                  value={formData.linkedinUrl}
                  onChange={(e) => updateFormData('linkedinUrl', e.target.value)}
                  helperText={intl.formatMessage({ id: 'hiring.post_form.helper.linkedin_profile' })}
                />
              </div>
            </div>

            {/* Application Instructions */}
            <div className="border-t border-neutral-200 pt-6">
              <label className="block text-sm font-medium text-neutral-900 mb-2">
                {intl.formatMessage({ id: 'hiring.post_form.field.application_instructions' })}
              </label>
              <textarea
                rows={4}
                value={formData.applicationInstructions}
                onChange={(e) => updateFormData('applicationInstructions', e.target.value)}
                placeholder={intl.formatMessage({ id: 'hiring.post_form.placeholder.application_instructions' })}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-sm text-neutral-500 mt-2">{intl.formatMessage({ id: 'hiring.post_form.helper.application_instructions' })}</p>
            </div>

            {/* Status */}
            <div className="border-t border-neutral-200 pt-6">
              <label className="block text-sm font-medium text-neutral-900 mb-3">
                {intl.formatMessage({ id: 'hiring.post_form.field.post_status' })}
              </label>
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <label className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    formData.status === 'draft'
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-neutral-200 hover:bg-neutral-50'
                  }`}>
                    <input
                      type="radio"
                      name="status"
                      value="draft"
                      checked={formData.status === 'draft'}
                      onChange={(e) => updateFormData('status', e.target.value)}
                      className="h-4 w-4 text-blue-600"
                    />
                    <div>
                      <div className="font-medium text-neutral-900">
                        {intl.formatMessage({ id: 'hiring.post_form.status.draft' })}
                      </div>
                      <div className="text-xs text-neutral-600">Hidden from candidates</div>
                    </div>
                  </label>

                  <label className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    formData.status === 'published'
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-neutral-200 hover:bg-neutral-50'
                  }`}>
                    <input
                      type="radio"
                      name="status"
                      value="published"
                      checked={formData.status === 'published'}
                      onChange={(e) => updateFormData('status', e.target.value)}
                      className="h-4 w-4 text-blue-600"
                    />
                    <div>
                      <div className="font-medium text-neutral-900">
                        {intl.formatMessage({ id: 'hiring.post_form.status.published' })}
                      </div>
                      <div className="text-xs text-neutral-600">Visible to candidates</div>
                    </div>
                  </label>

                  {isEditing && (
                    <label className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      formData.status === 'closed'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-neutral-200 hover:bg-neutral-50'
                    }`}>
                      <input
                        type="radio"
                        name="status"
                        value="closed"
                        checked={formData.status === 'closed'}
                        onChange={(e) => updateFormData('status', e.target.value)}
                        className="h-4 w-4 text-blue-600"
                      />
                      <div>
                        <div className="font-medium text-neutral-900">Closed</div>
                        <div className="text-xs text-neutral-600">No longer accepting interest</div>
                      </div>
                    </label>
                  )}
                </div>

                <div className="flex items-center gap-4">
                <Badge variant={formData.status === 'published' ? 'success' : 'warning'}>
                  {formData.status === 'published'
                    ? intl.formatMessage({ id: 'hiring.post_form.status.published' })
                    : formData.status === 'closed'
                      ? 'Closed'
                      : intl.formatMessage({ id: 'hiring.post_form.status.draft' })}
                </Badge>
                <p className="text-sm text-neutral-600">
                  {formData.status === 'published'
                    ? intl.formatMessage({ id: 'hiring.post_form.status.published_description' })
                    : formData.status === 'closed'
                      ? 'This post is closed and no longer visible in active opportunities.'
                      : intl.formatMessage({ id: 'hiring.post_form.status.draft_description' })}
                </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Preview Mode */
          <div className="bg-white border border-neutral-200 rounded-lg p-6">
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                {intl.formatMessage({ id: 'hiring.post_form.preview.notice' })}
              </p>
            </div>

            <div className="space-y-6">
              <div>
                <Badge
                  variant={formData.positionType === 'internship' ? 'internship' : 'full-time'}
                  className="mb-3"
                >
                  {formData.positionType === 'internship'
                    ? intl.formatMessage({ id: 'hiring.post_form.position_type.internship' })
                    : intl.formatMessage({ id: 'hiring.post_form.position_type.full_time' })}
                </Badge>
                <h2 className="text-2xl font-bold text-neutral-900 mb-3">
                  {formData.title || intl.formatMessage({ id: 'hiring.post_form.preview.position_title_placeholder' })}
                </h2>
                <div className="text-neutral-600 space-y-1">
                  <p>
                    {formData.location || intl.formatMessage({ id: 'hiring.post_form.preview.location_placeholder' })}{' '}
                    {formData.remote && `• ${intl.formatMessage({ id: 'hiring.post_form.field.remote_option' })}`}
                  </p>
                  {formData.firmName && (
                    <p className="flex items-center gap-2">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      {formData.firmName}
                    </p>
                  )}
                </div>
              </div>

              {formData.description && (
                <div>
                  <h3 className="font-semibold text-neutral-900 mb-2">{intl.formatMessage({ id: 'hiring.post_form.preview.section.description' })}</h3>
                  <p className="text-neutral-600 whitespace-pre-wrap">
                    {formData.description}
                  </p>
                </div>
              )}

              {formData.requirements && (
                <div>
                  <h3 className="font-semibold text-neutral-900 mb-2">{intl.formatMessage({ id: 'hiring.post_form.preview.section.requirements' })}</h3>
                  <p className="text-neutral-600 whitespace-pre-wrap">
                    {formData.requirements}
                  </p>
                </div>
              )}

              {formData.qualifications && (
                <div>
                  <h3 className="font-semibold text-neutral-900 mb-2">
                    {intl.formatMessage({ id: 'hiring.post_form.preview.section.qualifications' })}
                  </h3>
                  <p className="text-neutral-600 whitespace-pre-wrap">
                    {formData.qualifications}
                  </p>
                </div>
              )}

              {(formData.duration || formData.startDate || formData.compensation) && (
                <div>
                  <h3 className="font-semibold text-neutral-900 mb-2">{intl.formatMessage({ id: 'hiring.post_form.preview.section.position_details' })}</h3>
                  <div className="space-y-1 text-sm text-neutral-600">
                    {formData.duration && <p>{intl.formatMessage({ id: 'hiring.post_form.preview.detail.duration' }, { value: formData.duration })}</p>}
                    {formData.startDate && <p>{intl.formatMessage({ id: 'hiring.post_form.preview.detail.start_date' }, { value: formData.startDate })}</p>}
                    {formData.compensation && <p>{intl.formatMessage({ id: 'hiring.post_form.preview.detail.compensation' }, { value: formData.compensation })}</p>}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

      {/* Fixed Bottom Actions */}
      <div className="fixed bottom-0 left-0 right-0 bg-succession-bg-primary border-t border-succession-border p-4 md:p-6 shadow-succession-lg z-10">
        <div className="container mx-auto max-w-4xl flex gap-3">
          {isEditing && (
            <Button variant="ghost" size="lg" onClick={handleDelete} className="text-red-600 hover:bg-red-50">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              {intl.formatMessage({ id: 'hiring.post_form.actions.delete' })}
            </Button>
          )}
          <Button
            variant="outline"
            size="lg"
            onClick={handleSaveDraft}
            disabled={isSaving}
            className="flex-1"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
            </svg>
            {intl.formatMessage({ id: 'hiring.post_form.actions.save_draft' })}
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handlePublish}
            disabled={isSaving}
            className="flex-1"
          >
            {formData.status === 'closed'
              ? 'Close Post'
              : isEditing
                ? intl.formatMessage({ id: 'hiring.post_form.actions.update_post' })
                : intl.formatMessage({ id: 'hiring.post_form.actions.publish_post' })}
          </Button>
        </div>
      </div>
    </SingleColumnTemplate>
  );
};

export default HiringPostForm;
